import { isPaidPlan, ProductUsagePersona } from '@onshape/www-inertia';
import type { PersonalizationVariable } from '~/sanity/types';

/** Determine which Product Usage Persona the user has */
const productUsagePersona = computed(() => {
  const { user } = useCrossSession();
  const storage = useWwwStorage();
  const personalizationStorage = storage.personalizationProfileStorable.value;

  if (!personalizationStorage) return ProductUsagePersona.Default;

  const expiredLocalStorage =
    personalizationStorage.lastUpdated < Date.now() - 1000 * 60 * 60 * 24 * 30;

  const hasUserData = user.value !== null;

  // isAcquired means that they have logged into the product at least once
  // const isAcquired =
  //   personalizationStorage.signupDecoration ?
  //     personalizationStorage.signupDecoration.stage !== 'never-logged-in'
  //     // eslint-disable-next-line indent
  //   : storage.relevantCookies.value.some(
  //       (cookie) => cookie.name === 'x-www-have-seen-on' && !!cookie.value
  //     );

  if (expiredLocalStorage || !hasUserData) {
    return ProductUsagePersona.Default;
  }

  // check if disco-not-acquired. This has to be done before checking SSO data
  // if (!hasUserData || !isAcquired) {
  //   const isDiscoFromSignupDecoration =
  //     personalizationStorage.signupDecoration &&
  //     personalizationStorage.signupDecoration.planId.toLowerCase().includes('disco');
  //   const isTrialFromSignupDecoration =
  //     personalizationStorage.signupDecoration &&
  //     personalizationStorage.signupDecoration.planId.toLowerCase().includes('trial');

  //   const isDiscoNotAcquired =
  //     !isAcquired && (isDiscoFromSignupDecoration || isTrialFromSignupDecoration);
  //   if (isDiscoNotAcquired) {
  //     return ProductUsagePersona.DiscoNotAcquired;
  //   }
  // }

  // const { productUsage } = personalizationStorage.contactFields;
  const { currentUserData } = personalizationStorage;

  if (!currentUserData) {
    return ProductUsagePersona.Default;
  }
  const { activePlanId, activePlanGroup, ownPlanId, ownPlanGroup } =
    personalizationStorage.currentUserData;
  // const { uUsageTimeTotal } = productUsage;

  // isActivated means that they have used the product for more than an hour
  // const isActivated = uUsageTimeTotal ? uUsageTimeTotal >= 1 : false;

  // if we have no activePlanGroup or activePlanId, we can make no decisions about them, return default
  if (!activePlanGroup || !activePlanId || !ownPlanId) {
    return ProductUsagePersona.Default;
  }

  const isDiscoActivePlan = activePlanId.toLowerCase().includes('disco');
  const isTrialActivePlan = activePlanGroup.toLowerCase().includes('trial');
  const isFreeActivePlan = activePlanId.toLowerCase().includes('free');
  const isDiscoOwnPlan = ownPlanId.toLowerCase().includes('disco');

  const { activeTrialInfo, lastTrialInfo } = personalizationStorage.currentUserData;
  // isExpired is true if activePlanId === FREE && ownPlanId === DISCO
  // and means that they are in a trial or Disco that has 0 days left
  const isPlanMatch = activePlanId === ownPlanId;
  const hasLastTrialInfo = lastTrialInfo && lastTrialInfo.trialDaysRemaining !== undefined;
  const lastTrialExpired = hasLastTrialInfo && lastTrialInfo.trialDaysRemaining <= 0;
  const hasActiveTrialInfo = activeTrialInfo && activeTrialInfo.trialDaysRemaining !== undefined;
  const activeTrialExpired = hasActiveTrialInfo && activeTrialInfo.trialDaysRemaining <= 0;
  const hasZeroDaysLeft = lastTrialExpired || activeTrialExpired;
  const isExpired = !isPlanMatch && isDiscoOwnPlan && isFreeActivePlan && hasZeroDaysLeft;

  if (isExpired) return ProductUsagePersona.Expired;

  // if isDisco, check if they are acquired again, otherwise check if they are activated
  if (isDiscoActivePlan || isTrialActivePlan) {
    // legacy handling different versions of disco persona
    // if (!isAcquired) return ProductUsagePersona.DiscoNotAcquired;
    // if (isActivated) return ProductUsagePersona.DiscoActivated;
    // return ProductUsagePersona.DiscoNotActivated;
    return ProductUsagePersona.Disco;
  }

  // isPaidPlan means that we see an activePlanGroup that is a paid plan
  const hasPaidPlan = isPaidPlan(activePlanGroup) || isPaidPlan(ownPlanGroup); // handle both active plan group and own plan group
  if (hasPaidPlan) return ProductUsagePersona.Paid;

  if (!hasPaidPlan) {
    if (activePlanGroup === 'EDU Educator' || activePlanGroup === 'EDU Standard')
      return ProductUsagePersona.Education;
    return ProductUsagePersona.Free;
  }

  return ProductUsagePersona.Default;
});

/** Swap out the value of a personalization variable with the appropriate personalized value
 * or return the key of the value if it doesn't exist
 * @param personalizationVariable - The variable to swap out
 * @returns A ref containing the personalized value
 */
const personalizeVariableBlock = (personalizationVariable: PersonalizationVariable) => {
  const storage = useWwwStorage();
  const { user, setParamsForFetch } = useCrossSession();
  setParamsForFetch({
    params: ['currentUserData', 'contactFields'],
  });

  const { persistedProgressiveProfile, contactFields } =
    storage.personalizationProfileStorable.value || {};

  return computed(() => {
    const valMap: Record<string, string> = {
      firstName: user.value?.firstName || persistedProgressiveProfile?.firstName || '',
      lastName: user.value?.lastName || persistedProgressiveProfile?.lastName || '',
      csmName: user.value?.eloquaExtraData?.csmName || contactFields?.csmName || '',
      csmEmail: user.value?.eloquaExtraData?.csmEmail || contactFields?.csmEmail || '',
    };

    return valMap[personalizationVariable.value] || personalizationVariable.value;
  });
};

const init = () => {
  const { setParamsForFetch } = useCrossSession();
  setParamsForFetch({
    params: [
      'heartbeat',
      'currentUserData',
      'contactFields',
      'emailPreferences',
      'refCodeData',
      'eloquaExtraData',
    ],
  });
};

const isPaid = computed(() => {
  return productUsagePersona.value === ProductUsagePersona.Paid;
});

const countryPersona = computed(() => {
  const storage = useWwwStorage();
  const personalizationStorage = storage.personalizationProfileStorable.value;

  if (!personalizationStorage) return 'default';

  const expiredLocalStorage =
    personalizationStorage.lastUpdated < Date.now() - 1000 * 60 * 60 * 24 * 30;

  const { heartbeatData } = personalizationStorage;

  if (!heartbeatData || expiredLocalStorage) {
    return 'default';
  }

  const { countryCode } = heartbeatData;

  return countryCode || 'default';
});

/**  by US state */
const statePersona = computed(() => {
  const storage = useWwwStorage();
  const personalizationStorage = storage.personalizationProfileStorable.value;

  if (!personalizationStorage) return 'default';

  const expiredLocalStorage =
    personalizationStorage.lastUpdated < Date.now() - 1000 * 60 * 60 * 24 * 30;

  const { heartbeatData } = personalizationStorage;

  if (!heartbeatData || expiredLocalStorage) {
    return 'default';
  }

  const { region } = heartbeatData;

  return region || 'default';
});

/** By business region */
const regionPersona = computed(() => {
  const countryCode = countryPersona.value;

  if (!countryCode) {
    return 'default';
  }

  // TODO check for exact lists and get from DB
  const northAmericaCountries = ['US', 'CA', 'MX'];
  const emeaCountries = [
    'GB',
    'FR',
    'DE',
    'IT',
    'ES',
    'NL',
    'SE',
    'NO',
    'DK',
    'FI',
    'PL',
    'BE',
    'CH',
    'AT',
  ];
  const apacCountries = ['AU', 'NZ', 'JP', 'CN', 'IN', 'SG', 'KR'];

  if (northAmericaCountries.includes(countryCode)) {
    return 'north-america';
  } else if (emeaCountries.includes(countryCode)) {
    return 'emea';
  } else if (apacCountries.includes(countryCode)) {
    return 'apac';
  } else {
    return 'default';
  }
});

export const usePersonalization = () => {
  init();

  return {
    isPaid,
    productUsagePersona,
    countryPersona,
    regionPersona,
    statePersona,
    personalizeVariableBlock,
  };
};
